import { ANCHOR, AnchorProp, Drawer as BaseDrawer, SIZE } from 'baseui/drawer';
import { SizeProp } from 'baseui/modal';
import zIndex from 'common/constants/z-index';
import { themedUseStyletron } from 'common/styles/styletron.theme';
import React, {
  useImperativeHandle,
  useState,
  useCallback,
  useMemo,
} from 'react';

interface CustomOption {
  render: (close: () => void) => React.ReactNode;
  desktopDrawerWidthPercentage?: number;
  anchor?: AnchorProp;
  size?: SizeProp;
  backdropClose?: boolean;
  onBackdropClose?: () => void;
}
interface Option {
  type: 'custom';
  option: CustomOption;
  onClose: () => void;
}

export interface DrawerHandler {
  showCustom: (option: CustomOption) => void;
}

function Drawer(props: any, ref: React.Ref<DrawerHandler>) {
  const [state, setState] = useState<Option | undefined>();
  const [, theme] = themedUseStyletron();
  const option = state && state.option;
  const type = state && state.type;
  const onClose = useCallback(() => {
    setState(undefined);
  }, [setState]);
  useImperativeHandle(ref, () => ({
    showCustom(customOption) {
      setState({
        type: 'custom',
        option: customOption,
        onClose,
      });
    },
  }));
  const result = useMemo(() => {
    if (type === 'custom') {
      const { render } = option as CustomOption;
      return render(onClose);
    }
    return null;
  }, [type, option, onClose]);
  if (state) {
    switch (state.type) {
      case 'custom':
        return (
          <BaseDrawer
            isOpen={!!state}
            animate
            // eslint-disable-next-line jsx-a11y/no-autofocus
            showBackdrop
            // TODO: check if there any problem if the value is true
            closeable
            onBackdropClick={() => {
              if (option?.backdropClose) {
                option?.onBackdropClose?.();
                onClose();
              }
            }}
            anchor={option?.anchor}
            size={option?.size?.toString() || SIZE.default}
            overrides={{
              Root: {
                style: {
                  zIndex: zIndex.drawerBackdropZIndex,
                },
              },
              DrawerBody: {
                style: {
                  margin: '0px !important',
                  display: 'flex',
                  flexDirection: 'column',
                },
              },
              DrawerContainer: {
                style: () => ({
                  width: '100%',
                  ...(option?.anchor === ANCHOR.bottom && {
                    borderRadius: '8px 8px 0 0 !important',
                    overflow: 'hidden',
                  }),
                  ...(option?.anchor !== ANCHOR.bottom && {
                    [theme.mediaQuery.lg]: {
                      width: option?.desktopDrawerWidthPercentage
                        ? `${option?.desktopDrawerWidthPercentage}%`
                        : '60%',
                    },
                  }),
                }),
              },
              Close: {
                style: {
                  display: 'none',
                },
              },
            }}
          >
            {result}
          </BaseDrawer>
        );
    }
  }
  return null;
}

export default React.forwardRef(Drawer);
